import { defineStore } from 'pinia'
import { computed, ref } from 'vue'
import { SAVED_DECK_LIMIT } from '@shared/index'

export const MAX_SAVED_DECKS = SAVED_DECK_LIMIT

export type DeckSlot = {
  id: string
  name: string
  mainDeckCards: any[]
  extraDeckCards: any[]
}

type PersistedDeckState = {
  decks?: DeckSlot[]
  activeId?: string | null
  editingId?: string | null
  mainDeckCards?: any[]
  extraDeckCards?: any[]
  deckName?: string
}

const DEFAULT_DECK_NAME = 'My Yu-Gi-Oh Deck'

/** Saved baraja held for a tournament. Not part of the persisted deck list. */
export const tournamentDeckLockId = ref<string | null>(null)

export function setTournamentDeckLock(id: string | null): void {
  tournamentDeckLockId.value = id
}

export function tournamentDeckLocked(id: string | null | undefined): boolean {
  return Boolean(id && tournamentDeckLockId.value === id)
}

function newDeckId(): string {
  const web = globalThis.crypto
  if (web && typeof web.randomUUID === 'function') return web.randomUUID()
  if (web && typeof web.getRandomValues === 'function') {
    const bytes = web.getRandomValues(new Uint8Array(16))
    bytes[6] = (bytes[6] & 0x0f) | 0x40
    bytes[8] = (bytes[8] & 0x3f) | 0x80
    const hex = [...bytes].map((b) => b.toString(16).padStart(2, '0')).join('')
    return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`
  }
  const n = Date.now().toString(16).padStart(12, '0')
  return `00000000-0000-4000-8000-${n.slice(-12)}`
}

function cloneCards(cards: any[]): any[] {
  return cards.map((card) => ({ ...card }))
}

function makeSlot(
  name: string,
  main: any[] = [],
  extra: any[] = [],
  id?: string,
): DeckSlot {
  return {
    id: id ?? newDeckId(),
    name: name.trim() || DEFAULT_DECK_NAME,
    mainDeckCards: cloneCards(main),
    extraDeckCards: cloneCards(extra),
  }
}

/**
 * Turns the old single-deck persist payload into slots.
 */
export function migrateDeckPersist(raw: unknown): {
  decks: DeckSlot[]
  activeId: string | null
  editingId: string | null
} {
  const data = raw && typeof raw === 'object'
    ? (raw as PersistedDeckState)
    : {}
  if (Array.isArray(data.decks) && data.decks.length > 0) {
    const usedIds = new Set<string>()
    const decks = data.decks.slice(0, MAX_SAVED_DECKS).map((slot) => {
      const rawId = slot?.id ? String(slot.id) : ''
      const id = rawId && !usedIds.has(rawId) ? rawId : undefined
      const next = makeSlot(
        String(slot?.name || DEFAULT_DECK_NAME),
        Array.isArray(slot?.mainDeckCards) ? slot.mainDeckCards : [],
        Array.isArray(slot?.extraDeckCards) ? slot.extraDeckCards : [],
        id,
      )
      usedIds.add(next.id)
      return next
    })
    const ids = new Set(decks.map((d) => d.id))
    const activeId = data.activeId && ids.has(data.activeId)
      ? data.activeId
      : decks[0]!.id
    const editingId = data.editingId && ids.has(data.editingId)
      ? data.editingId
      : activeId
    return { decks, activeId, editingId }
  }
  const slot = makeSlot(
    String(data.deckName || DEFAULT_DECK_NAME),
    Array.isArray(data.mainDeckCards) ? data.mainDeckCards : [],
    Array.isArray(data.extraDeckCards) ? data.extraDeckCards : [],
  )
  return { decks: [slot], activeId: slot.id, editingId: slot.id }
}

export const useDeckStore = defineStore(
  'deck',
  () => {
    const initial = makeSlot(DEFAULT_DECK_NAME)
    const decks = ref<DeckSlot[]>([initial])
    const activeId = ref<string | null>(initial.id)
    const editingId = ref<string | null>(initial.id)

    const activeSlot = computed(
      () => decks.value.find((d) => d.id === activeId.value) ?? null,
    )
    const editingSlot = computed(
      () => decks.value.find((d) => d.id === editingId.value) ?? null,
    )
    const canCreate = computed(() => decks.value.length < MAX_SAVED_DECKS)

    function ensureIntegrity(): void {
      if (decks.value.length === 0) {
        const slot = makeSlot(DEFAULT_DECK_NAME)
        decks.value = [slot]
        activeId.value = slot.id
        editingId.value = slot.id
        return
      }
      const ids = new Set(decks.value.map((d) => d.id))
      if (!activeId.value || !ids.has(activeId.value)) {
        activeId.value = decks.value[0]!.id
      }
      if (!editingId.value || !ids.has(editingId.value)) {
        editingId.value = activeId.value
      }
    }

    function slotById(id: string): DeckSlot | undefined {
      return decks.value.find((d) => d.id === id)
    }

    function setEditingId(id: string): void {
      if (!slotById(id)) return
      editingId.value = id
    }

    function setActiveId(id: string): void {
      if (!slotById(id)) return
      activeId.value = id
    }

    /**
     * Writes the open editor list into the editing slot.
     */
    function saveDeck(mainDeck: any[], extraDeck: any[], name: string): void {
      ensureIntegrity()
      const id = editingId.value
      if (!id || tournamentDeckLocked(id)) return
      decks.value = decks.value.map((slot) =>
        slot.id === id
          ? makeSlot(name, mainDeck, extraDeck, id)
          : slot,
      )
    }

    function createDeck(name: string = DEFAULT_DECK_NAME): string | null {
      ensureIntegrity()
      if (decks.value.length >= MAX_SAVED_DECKS) return null
      const slot = makeSlot(name)
      decks.value = [...decks.value, slot]
      editingId.value = slot.id
      return slot.id
    }

    function duplicateDeck(id: string, name: string): string | null {
      ensureIntegrity()
      if (decks.value.length >= MAX_SAVED_DECKS) return null
      const src = slotById(id)
      if (!src) return null
      const slot = makeSlot(name, src.mainDeckCards, src.extraDeckCards)
      decks.value = [...decks.value, slot]
      editingId.value = slot.id
      return slot.id
    }

    function deleteDeck(id: string): void {
      ensureIntegrity()
      if (tournamentDeckLocked(id)) return
      const src = slotById(id)
      if (!src) return
      if (decks.value.length === 1) {
        const slot = makeSlot(DEFAULT_DECK_NAME, [], [], src.id)
        decks.value = [slot]
        activeId.value = slot.id
        editingId.value = slot.id
        return
      }
      const wasActive = activeId.value === id
      const wasEditing = editingId.value === id
      const remaining = decks.value.filter((d) => d.id !== id)
      decks.value = remaining
      if (wasActive) {
        activeId.value = remaining[0]!.id
      }
      if (wasEditing) {
        editingId.value = wasActive ? activeId.value : remaining[0]!.id
      }
    }

    function renameDeck(id: string, name: string): void {
      if (tournamentDeckLocked(id)) return
      const next = name.trim() || DEFAULT_DECK_NAME
      decks.value = decks.value.map((slot) =>
        slot.id === id ? { ...slot, name: next } : slot,
      )
    }

    /**
     * Applies a mapper to every copy in every saved baraja (foil schema).
     */
    function rewriteAllCards(map: (cards: any[]) => any[]): void {
      decks.value = decks.value.map((slot) => ({
        ...slot,
        mainDeckCards: map(slot.mainDeckCards),
        extraDeckCards: map(slot.extraDeckCards),
      }))
    }

    function clearDeck(): void {
      const slot = makeSlot(DEFAULT_DECK_NAME)
      decks.value = [slot]
      activeId.value = slot.id
      editingId.value = slot.id
    }

    /**
     * Replaces local slots with the account snapshot.
     */
    function hydrateFromServer(
      slots: DeckSlot[],
      nextActive: string | null,
    ): void {
      if (slots.length === 0) {
        clearDeck()
        return
      }
      decks.value = slots.map((slot) =>
        makeSlot(slot.name, slot.mainDeckCards, slot.extraDeckCards, slot.id),
      )
      const ids = new Set(decks.value.map((d) => d.id))
      activeId.value =
        nextActive && ids.has(nextActive) ? nextActive : decks.value[0]!.id
      editingId.value = activeId.value
    }

    return {
      decks,
      activeId,
      editingId,
      activeSlot,
      editingSlot,
      canCreate,
      ensureIntegrity,
      setEditingId,
      setActiveId,
      saveDeck,
      createDeck,
      duplicateDeck,
      deleteDeck,
      renameDeck,
      rewriteAllCards,
      clearDeck,
      hydrateFromServer,
    }
  },
)
